// „Оборот по сервитьори" — заема мястото на натовареността по час.
//
// Кой колко е направил е това, което управителят гледа всеки ден; часът на
// затваряне е любопитен, но не води до действие. Редовете са подредени по
// оборот, лентата показва дела спрямо ПЪРВИЯ, не спрямо общото — така
// разликата между първи и втори се чете веднага.

import { useTranslation } from "react-i18next";

import { useReportLink } from "../useReportLink";
import { RightOutlined } from "@ant-design/icons";

import { fmtEuro } from "../../../../utils/currency";
import { parseDecimal } from "../../../../components/dataTable";
import type { DashboardWaiterAmount } from "../../../../api/reportsDashboard";
import { pluralLabel } from "../reportPlural";

type Props = {
  waiters: DashboardWaiterAmount[];
  /** Оборотът за периода — за дела на всеки сервитьор. */
  totalRevenue: number;
};

export const WaitersRevenueCard = ({ waiters, totalRevenue }: Props) => {
  const { t } = useTranslation();
  const openReport = useReportLink("waiters");

  const rows = waiters.map((waiter) => ({
    ...waiter,
    value: parseDecimal(waiter.amount),
  }));
  const top = rows.length ? rows[0].value : 0;

  return (
    <section className="flex min-w-0 flex-col rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
          {t("reportsPage.dashWaitersTitle")}
        </h2>
        {openReport ? (
          <button
            type="button"
            onClick={openReport}
            className="flex items-center gap-1 text-[12px] font-bold text-brand-deep-ink hover:underline"
          >
            {t("reportsPage.dashFullReport")}
            <RightOutlined className="text-[9px]" />
          </button>
        ) : null}
      </header>

      {rows.length ? (
        <ul className="flex flex-col gap-2.5">
          {rows.map((waiter, index) => {
            const share = top > 0 ? (waiter.value / top) * 100 : 0;
            const ofTotal =
              totalRevenue > 0 ? (waiter.value / totalRevenue) * 100 : 0;
            return (
              <li key={waiter.waiter_id} className="flex flex-col gap-1">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                    {waiter.name}
                  </span>
                  <span className="shrink-0 font-pos-mono text-[11px] font-semibold tabular-nums text-faint">
                    {`${Math.round(ofTotal)} %`}
                  </span>
                  <span className="shrink-0 font-pos-mono text-[13px] font-bold tabular-nums text-ink">
                    {fmtEuro(waiter.value)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-1 flex-1 overflow-hidden rounded-full bg-fill-2">
                    {/* Дялът е известен чак по време на изпълнение. */}
                    <span
                      className={`block h-full rounded-full ${
                        index === 0 ? "bg-brand" : "bg-brand-tint2"
                      }`}
                      style={{ width: `${share}%` }}
                    />
                  </span>
                  <span className="shrink-0 font-pos-mono text-[10.5px] font-semibold tabular-nums text-muted">
                    {pluralLabel(
                      t,
                      waiter.count,
                      "reportsPage.dashBillsCountOne",
                      "reportsPage.dashBillsCount",
                    )}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="py-6 text-center text-[12.5px] font-medium text-muted">
          {t("reportsPage.dashNoData")}
        </p>
      )}
    </section>
  );
};
